/**
 * Client for the Tailor "why" narration SSE endpoint (FXC-9009).
 *
 * The backend (routes/narration.py) streams a 1-2 sentence explanation of WHY
 * the high-lift sampler chose a point. We stream it over `fetch` + a
 * ReadableStream reader (not EventSource) because the request needs an
 * Authorization header — EventSource can't set one.
 *
 * READ-ONLY: this narrates choices already made; nothing here influences which
 * point the sampler picks.
 *
 * `useNarrationStream` adds the UI-side discipline:
 *   - per-point CACHE (keyed by a stable condition key) so replaying / revisiting
 *     a point never regenerates — narration is deterministic-enough to reuse and
 *     Ollama is not free;
 *   - drop-to-latest debounce: at most ONE stream in flight; a fast autoplay
 *     cadence that outruns the model coalesces onto the newest requested point
 *     instead of stampeding the single-worker API / local Ollama.
 */

import { getAuthToken } from "./client";
import { type ReplayStep } from "../features/highlift/workbench/campaignMath";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

/** The grounding bundle POSTed to /highlift/narrate — mirrors NarrateRequest. */
export interface NarrationBundle {
  condition?: Record<string, number | string>;
  reasons?: string[];
  score?: number | null;
  purpose?: string | null;
  engine?: string | null;
  coverage_before?: number | null;
  coverage_after?: number | null;
  result?: {
    CL?: number | null;
    CD?: number | null;
    CY?: number | null;
    Cl?: number | null;
    Cm?: number | null;
    Cn?: number | null;
  } | null;
  backend?: string | null;
  components?: Record<string, number> | null;
  classification?: string | null;
  disagreement_z?: number | null;
  /** Active reflection prior on the producing campaign. The backend grounds
   * the model in the coupled aircraft-plane transform (beta flips together
   * with aileron/rudder/P/R; CY/Cl/Cn change sign) so the narration never
   * describes a beta-only mirror. Omit (or mode "off") when no prior is
   * active — the model is then forbidden from mentioning symmetry. */
  symmetry?: { mode: "off" | "mirror" | "half-and-mirror"; axes: string[] } | null;
}

export type NarrationStatus = "idle" | "streaming" | "done" | "offline" | "error";

/** Callbacks a raw stream reports into. */
export interface NarrationStreamHandlers {
  onModel?: (model: string) => void;
  onDelta: (delta: string) => void;
  onOffline?: (reason: string) => void;
  onError?: (reason: string) => void;
  onDone?: () => void;
}

/**
 * Stream a narration for one bundle, invoking handlers as events arrive.
 *
 * Resolves when the stream ends (DONE, offline, error, or abort). A 503 is NOT
 * an error — it means Ollama is unreachable (e.g. the AWS deploy), so we invoke
 * `onOffline` and the UI shows a subtle pill, never a hard failure.
 */
export async function streamNarration(
  bundle: NarrationBundle,
  handlers: NarrationStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/highlift/narrate`, {
      method: "POST",
      headers,
      body: JSON.stringify(bundle),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) return;
    handlers.onError?.("network error");
    return;
  }

  if (res.status === 503) {
    let reason = "narration offline";
    try {
      const j = await res.json();
      if (j?.reason) reason = String(j.reason);
    } catch {
      /* keep default */
    }
    handlers.onOffline?.(reason);
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError?.(`narration failed (${res.status})`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // Parse the SSE frame buffer: events are separated by a blank line; each
  // event's payload is the concatenation of its `data:` lines.
  const flush = (chunk: string): boolean => {
    buffer += chunk;
    let sep: number;
    // eslint-disable-next-line no-cond-assign
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const data = frame
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trimStart())
        .join("\n");
      if (!data) continue;
      if (data === "[DONE]") {
        handlers.onDone?.();
        return true; // signal caller to stop reading
      }
      try {
        const obj = JSON.parse(data) as {
          model?: string;
          delta?: string;
          error?: string;
        };
        if (obj.model) handlers.onModel?.(obj.model);
        if (obj.delta) handlers.onDelta(obj.delta);
        if (obj.error) handlers.onError?.(obj.error);
      } catch {
        /* skip an unparseable frame rather than abort the stream */
      }
    }
    return false;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (flush(decoder.decode(value, { stream: true }))) break;
    }
  } catch (err) {
    if (!signal?.aborted) handlers.onError?.("stream interrupted");
  } finally {
    try {
      await reader.cancel();
    } catch {
      /* ignore */
    }
  }
}

// ---------------------------------------------------------------------------
// useNarrationStream — per-point cache + drop-to-latest debounce
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseNarrationResult {
  /** Accumulated narration text for the current key (grows as tokens arrive). */
  text: string;
  status: NarrationStatus;
  /** The model that produced it (for the terminal caption), when known. */
  model: string | null;
}

/**
 * Build the stable cache key + grounding bundle for a replay step. The key is
 * the full condition (the same identity the sampler uses), so two visits to the
 * same point reuse one narration.
 */
export function bundleForStep(
  step: ReplayStep,
  extra?: { coverageBefore?: number | null },
): { key: string; bundle: NarrationBundle } {
  const condition: Record<string, number> = {
    alpha_deg: step.alphaDeg,
    flap_deploy_deg: step.deployDeg,
    flaperon_deflection_deg: step.flaperonDeg,
    mach: step.mach,
    re_chord_millions: step.reMillions,
    disk_thrust_loading: step.blowing,
  };
  // Deterministic key: sorted axis:value pairs at fixed precision.
  const key = Object.keys(condition)
    .sort()
    .map((k) => `${k}=${condition[k]!.toFixed(4)}`)
    .join("|");
  const bundle: NarrationBundle = {
    condition,
    // The step's own verbatim reasons (persisted or iteration-level); the joined
    // `reason` string is the last-resort fallback so a bare campaign still speaks.
    reasons: step.reasons?.length
      ? step.reasons
      : step.reason
        ? step.reason.split(" · ").filter(Boolean)
        : [],
    score: step.score,
    purpose: step.purpose,
    engine: step.engine,
    coverage_before: extra?.coverageBefore ?? null,
    coverage_after: step.coverageAfter,
    result: step.CL != null ? { CL: step.CL } : null,
    backend: step.backend,
    components: step.components,
    classification: step.classification,
    disagreement_z: step.disagreementZ,
  };
  return { key, bundle };
}

/**
 * Stream a narration for the CURRENT point (key + bundle), with a per-key cache
 * and a single-in-flight, drop-to-latest scheduler.
 *
 * Pass `null` to clear (idle). When `key` matches a cached completed narration
 * the text is returned instantly with no network call.
 */
export function useNarrationStream(
  request: { key: string; bundle: NarrationBundle } | null,
): UseNarrationResult {
  const [text, setText] = useState("");
  const [status, setStatus] = useState<NarrationStatus>("idle");
  const [model, setModel] = useState<string | null>(null);

  // Completed narrations, keyed by condition key — survives across points so a
  // replay loop never regenerates. Kept in a ref (mutating it must not itself
  // trigger a render; the state setters below do that).
  const cacheRef = useRef<Map<string, { text: string; model: string | null }>>(new Map());
  // The single in-flight controller + the key it is narrating.
  const activeRef = useRef<{ key: string; controller: AbortController } | null>(null);
  // The most-recently-requested key while a stream is busy (drop-to-latest).
  const pendingRef = useRef<{ key: string; bundle: NarrationBundle } | null>(null);
  // Once-offline stays offline for the session — no point hammering a backend
  // we already know is down (and it keeps the pill stable during autoplay).
  const offlineRef = useRef(false);

  // Abort + clear whatever stream is in flight (a new request supersedes it).
  // Prevents a slow foreign stream from writing shared state / the cache after
  // the cursor moved on, and stops it hitting the single-worker API needlessly.
  const abortActive = () => {
    if (activeRef.current) {
      activeRef.current.controller.abort();
      activeRef.current = null;
    }
  };

  const run = useCallback((req: { key: string; bundle: NarrationBundle }) => {
    // Cache hit — surface instantly, no network. A prior in-flight stream is
    // now stale (its point is off-screen): abort it so its later deltas can't
    // clobber this cached display (adversarial-review MAJOR).
    const cached = cacheRef.current.get(req.key);
    if (cached) {
      abortActive();
      pendingRef.current = null;
      setText(cached.text);
      setModel(cached.model);
      setStatus("done");
      return;
    }
    if (offlineRef.current) {
      // Same shape as the cache-hit path: a stream that started before offline
      // flipped must not keep writing over this offline display.
      abortActive();
      pendingRef.current = null;
      setText("");
      setStatus("offline");
      return;
    }
    // Busy: remember the latest wanted point and let the current stream finish
    // (drop-to-latest — a stale intermediate point is skipped entirely).
    if (activeRef.current) {
      pendingRef.current = req;
      return;
    }

    const controller = new AbortController();
    activeRef.current = { key: req.key, controller };
    let acc = "";
    let mdl: string | null = null;
    // Whether an error event arrived — an errored stream must NOT be cached
    // (else a revisit serves partial/empty text as "done"; review MINOR).
    let sawError = false;
    setText("");
    setModel(null);
    setStatus("streaming");

    // Is this callback still driving the current stream? A superseded stream
    // (aborted by a cache-hit/offline supersede) must not touch shared state.
    const isCurrent = () => activeRef.current?.controller === controller;

    const finish = (next: NarrationStatus) => {
      // Only commit state if this stream is still the active one.
      if (!isCurrent()) return;
      activeRef.current = null;
      setStatus(next);
      const queued = pendingRef.current;
      pendingRef.current = null;
      if (queued && queued.key !== req.key) run(queued);
    };

    void streamNarration(
      req.bundle,
      {
        onModel: (m) => {
          if (!isCurrent()) return; // superseded — do not clobber the display
          mdl = m;
          setModel(m);
        },
        onDelta: (d) => {
          if (!isCurrent()) return; // superseded — do not clobber the display
          acc += d;
          setText(acc);
        },
        onOffline: () => {
          if (!isCurrent()) return;
          offlineRef.current = true;
          setText("");
          finish("offline");
        },
        onError: () => {
          if (!isCurrent()) return;
          sawError = true;
          finish("error");
        },
        onDone: () => {
          // Cache ONLY a clean, still-current completion: never poison the key
          // with an errored stream's partial text or a superseded stream's acc.
          if (isCurrent() && !sawError) {
            cacheRef.current.set(req.key, { text: acc, model: mdl });
          }
          finish("done");
        },
      },
      controller.signal,
    );
  }, []);

  useEffect(() => {
    if (!request) {
      setStatus("idle");
      setText("");
      return;
    }
    run(request);
    // Depend on the KEY (stable identity), not the bundle object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.key, run]);

  // Abort any in-flight stream on unmount.
  useEffect(
    () => () => {
      activeRef.current?.controller.abort();
      activeRef.current = null;
    },
    [],
  );

  return { text, status, model };
}
